import { memo } from 'react';

import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import { type WorkspaceAgentPanelImageDimensions } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';

/** CSS pixels — onboarding fanned card (`aspect-[4/3]` in ~82px-wide frame). */
const ONBOARDING_ASSET_STACK_THUMBNAIL: WorkspaceAgentPanelImageDimensions = {
  fit: 'crop',
  height: 62,
  width: 82,
};

export const STACK_VISIBLE_LIMIT = 4;
const MAX_STACK_COUNT = 99;

type OnboardingStackLayout = {
  left: string;
  rotate: number;
  top: string;
  width: string;
  zIndex: number;
};

type OnboardingStackThumbnailProps = {
  isThumbnailPending: boolean;
  layout: OnboardingStackLayout;
  omitRotation: boolean;
  showCountBadge: boolean;
  thumbnailUrl: string | null;
  totalCount: number;
};

const STACK_LAYOUTS: readonly OnboardingStackLayout[] = [
  { left: '12%', rotate: -2.5, top: '14%', width: '74%', zIndex: 1 },
  { left: '13%', rotate: -1, top: '9%', width: '74%', zIndex: 2 },
  { left: '12%', rotate: 1, top: '4%', width: '74%', zIndex: 3 },
  { left: '12%', rotate: 2, top: '0%', width: '76%', zIndex: 4 },
];

export type PrivateWorkspaceAgentOnboardingAssetStackProps = {
  /** Keeps stack card keys apart when more than one stack is on screen. */
  idPrefix: string;
  /** Cards after the stills that shimmer while their file is still processing. */
  pendingThumbnailCount?: number;
  thumbnailUrls: readonly string[];
  /** Badge count. Extra rows past the stills are counted and not drawn. */
  totalCount: number;
};

/**
 * Fanned still stack for empty-thread visuals: onboarding, and the uploaded-batch offer.
 * Props-only thumbnail URLs — not workspace clips and not chat history.
 */
export const PrivateWorkspaceAgentOnboardingAssetStack = memo(
  ({
    idPrefix,
    pendingThumbnailCount = 0,
    thumbnailUrls,
    totalCount,
  }: PrivateWorkspaceAgentOnboardingAssetStackProps) => {
    if (totalCount === 0) {
      return null;
    }

    const visibleCount = Math.min(totalCount, STACK_VISIBLE_LIMIT);
    const frontIndex = visibleCount - 1;

    return (
      <div className="relative isolate flex h-[88px] w-[108px]" data-testid="WORKSPACE_AGENT_ONBOARDING_ASSET_STACK">
        {Array.from({ length: visibleCount }, (_, index) => (
          <OnboardingStackThumbnail
            key={`${idPrefix}-stack-${index}`}
            isThumbnailPending={index >= thumbnailUrls.length && index < thumbnailUrls.length + pendingThumbnailCount}
            layout={STACK_LAYOUTS[index]}
            omitRotation={visibleCount === 1}
            showCountBadge={index === frontIndex}
            thumbnailUrl={index < thumbnailUrls.length ? thumbnailUrls[index] : null}
            totalCount={totalCount}
          />
        ))}
      </div>
    );
  },
);

PrivateWorkspaceAgentOnboardingAssetStack.displayName = 'PrivateWorkspaceAgentOnboardingAssetStack';

const OnboardingStackThumbnail = memo(
  ({
    isThumbnailPending,
    layout,
    omitRotation,
    showCountBadge,
    thumbnailUrl,
    totalCount,
  }: OnboardingStackThumbnailProps) => {
    const transform = omitRotation ? undefined : `rotate(${layout.rotate}deg)`;

    return (
      <div
        className="absolute origin-[50%_88%] overflow-hidden rounded-8 border border-gray-1 shadow-s"
        style={{
          left: layout.left,
          top: layout.top,
          width: layout.width,
          zIndex: layout.zIndex,
          ...(transform !== undefined && { transform }),
        }}
      >
        <div className="relative aspect-[4/3]">
          {thumbnailUrl ? (
            <WorkspaceAgentPanelImage
              {...ONBOARDING_ASSET_STACK_THUMBNAIL}
              alt=""
              className="size-full object-cover"
              src={thumbnailUrl}
            />
          ) : (
            <div
              className={cn(
                'flex size-full items-center justify-center bg-gray-3 text-gray-9',
                isThumbnailPending && 'shimmer-background bg-gray-4',
              )}
              data-testid={isThumbnailPending ? 'WORKSPACE_AGENT_ONBOARDING_ASSET_STACK_PENDING' : undefined}
            />
          )}
          {showCountBadge ? <OnboardingStackCountBadge count={totalCount} /> : null}
        </div>
      </div>
    );
  },
);

OnboardingStackThumbnail.displayName = 'OnboardingStackThumbnail';

type OnboardingStackCountBadgeProps = {
  count: number;
};

const OnboardingStackCountBadge = memo(({ count }: OnboardingStackCountBadgeProps) => {
  const display = count > MAX_STACK_COUNT ? `${MAX_STACK_COUNT}+` : `${count}`;

  return (
    <span
      aria-hidden
      className="pointer-events-none absolute right-1 bottom-1 z-50 flex h-4 min-w-4 items-center justify-center rounded-full bg-gray-12 px-1 font-semibold text-8 text-gray-1 leading-none shadow-s"
      data-testid="WORKSPACE_AGENT_ONBOARDING_ASSET_STACK_COUNT"
    >
      {display}
    </span>
  );
});

OnboardingStackCountBadge.displayName = 'OnboardingStackCountBadge';
